import Service, { service } from '@ember/service';
import { queryManager } from 'ember-apollo-client';
import * as calendarCache from 'my-phorest/utils/calendar-cache';

import staffMemberQuery from 'my-phorest/gql/queries/staff-member.graphql';

/**
 * This is a map that maps entities with queries.
 * You'll also find a special entry here: $calendar. Invalidating the calendar requires more work than simple query invalidation and is handled by a handy util.
 */
const ENTITY_LIST_QUERIES_MAP = {
  appointmentBranchSettings: 'appointmentBranchSettings',
  appointmentPromptSettings: 'branchAppointmentPromptSettings',
  branch: 'branches',
  business: 'business',
  clientCategory: 'clientCategories',
  clientSource: 'clientSources',
  machine: ['machines', '$calendar'],
  openingHours: ['branchTimeSlots', '$calendar'],
  productCategory: 'productCategories',
  room: ['rooms', '$calendar'],
  rosters: ['$calendar'],
  service: [
    'services',
    'serviceCategories',
    'qualifiedStaffMembers',
    'categoryServicesTree',
  ],
  serviceCategory: [
    'serviceCategories',
    'chainServiceCategories',
    'categoryServicesTree',
  ],
  staff: ['staffConnection', 'staffCalendar', 'qualifiedStaffMembers'],
  staffCategory: 'staffCategories',
};

export default class ApolloCache extends Service {
  @service session;
  @queryManager apollo;

  get cache() {
    return this.apollo.apollo.client.cache;
  }

  getEntityName(resourceType, id) {
    const type = resourceType.charAt(0).toUpperCase() + resourceType.slice(1);
    return `${type}:${id}`;
  }

  async handleEmbeddedAppSignalForCacheInvalidation(data) {
    if (data.type === 'entity') {
      const entityName = this.getEntityName(data.resourceType, data.id);
      const cacheData = this.cache.extract();
      const isCached = !!cacheData[entityName];

      if (isCached) {
        this.cache.evict({ id: entityName });
        await this.reFetchSingleEntity(data.resourceType, data.id);
      }
    }

    this.invalidateList(data.resourceType);
    this.cache.gc();

    if (this.shouldUpdateSession(data.resourceType, data.id)) {
      await this.session.reloadCurrentUserSession();
    }
  }

  invalidateList(type) {
    const fieldName = ENTITY_LIST_QUERIES_MAP[type];

    if (fieldName) {
      const fieldNames = Array.isArray(fieldName) ? fieldName : [fieldName];

      fieldNames.forEach((fieldName) => {
        if (fieldName === '$calendar') {
          calendarCache.invalidate(this.cache);
        } else {
          this.cache.evict({ id: 'ROOT_QUERY', fieldName });
        }
      });
    }
  }

  async reFetchSingleEntity(type, id) {
    if (type === 'staff') {
      await this.apollo.query(
        {
          query: staffMemberQuery,
          variables: { id },
        },
        type
      );
    }
  }

  shouldUpdateSession(type, id) {
    switch (type) {
      case 'accessLevel':
        return this.session.currentUser?.staffMembers.some(
          (staffMember) => staffMember.accessLevel.id === id
        );
      case 'staff':
        return this.session.currentUser?.staffMembers.some(
          (staffMember) => staffMember.id === id
        );
      case 'user':
        return this.session.userId === id;
      case 'business':
      case 'branch':
        return true;
      default:
        return false;
    }
  }
}
